import React, { useState } from 'react';
import { useLicense } from '../contexts/LicenseContext';
import { getLicensingPortalUrl } from '../config/api-config';

interface CancelTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancellationSuccess: () => void;
}

const CancelTrialModal: React.FC<CancelTrialModalProps> = ({
  isOpen,
  onClose,
  onCancellationSuccess
}) => {
  const [error, setError] = useState<string | null>(null);
  const { licenseInfo } = useLicense();

  if (!isOpen) return null;

  const trial = licenseInfo?.trial;
  if (!trial) return null;

  const handleCancel = () => {
    // Open Licensing Server Customer Portal in new tab (env-aware: QA vs prod)
    window.open(getLicensingPortalUrl('/customer/login'), '_blank');
    // Close this modal
    handleClose();
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={handleClose}></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            {/* Warning icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <div className="text-center mb-4">
              <h3 className="text-2xl leading-6 font-bold text-gray-900 mb-2" id="modal-title">
                Cancel Trial Subscription
              </h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to cancel your trial?
              </p>
            </div>

            {/* Error alert */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button onClick={() => setError(null)} className="inline-flex text-red-400 hover:text-red-600">
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Trial details */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Current Trial Details:</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Tier:</span>
                  <span className="font-medium text-gray-900">{trial.selectedTier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-gray-900">{trial.priceFormatted}</span>
                </div>
                {trial.trialEndDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Trial Ends:</span>
                    <span className="font-medium text-gray-900">
                      {(() => {
                        const dateStr = trial.trialEndDate;
                        const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString();
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Authorization Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Secure Authorization Required</h4>
                  <p className="text-sm text-blue-800">
                    You will be redirected to the CAVU Licensing Server Customer Portal.
                    <strong className="block mt-1">The Licensing Server will verify that you have proper Customer Admin authorization before allowing trial cancellation.</strong>
                    <span className="block mt-1 text-xs">Only Customer Admin accounts are authorized to cancel trial subscriptions for security and billing protection.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* What happens */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Cancellation Process:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• You'll be redirected to the Customer Portal login page</li>
                <li>• Log in with your Customer Admin account</li>
                <li>• Cancel your trial from the dashboard</li>
                <li>• Your payment authorization will be voided</li>
                <li>• You will <strong>NOT</strong> be charged</li>
                <li>• Your license <strong>keeps working until the end of your trial period</strong> — same key, nothing to update</li>
                <li>• It then expires on its own; nothing renews</li>
              </ul>
            </div>

          </div>

          {/* Action buttons */}
          <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 mt-3 sm:mt-0"
            >
              Keep Trial
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Go to Licensing Server Portal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelTrialModal;
